/* Blind timer voice + sound utilities
   Ported from Seven Deuce Poker Club blind-timer.js */

let audioCtx = null;

function getAudioCtx() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtx;
}

/**
 * Play a beep or wooga sound
 * @param {'beep'|'wooga'} type
 * @param {number} durationMs
 */
export function playSound(type = 'beep', durationMs = 300) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'wooga') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(440, ctx.currentTime + durationMs / 2000);
      osc.frequency.linearRampToValueAtTime(220, ctx.currentTime + durationMs / 1000);
    } else {
      osc.type = 'sine';
      osc.frequency.value = 880;
    }

    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + durationMs / 1000);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
  } catch (e) {
    console.warn('Audio not available:', e);
  }
}

/**
 * Speak a text announcement using Web Speech API
 */
export function speakAnnouncement(text, voiceConfig = {}) {
  if (!('speechSynthesis' in window) || !text) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = voiceConfig.voiceRate ?? 1;
  u.pitch = voiceConfig.voicePitch ?? 1;
  u.volume = voiceConfig.voiceVolume ?? 1;
  window.speechSynthesis.speak(u);
}

/**
 * Build the announcement text for a level transition
 */
export function buildAnnouncement({ smallBlind, bigBlind, ante, isBreak }) {
  if (isBreak) return 'Break time. Take five.';
  let text = `New level. Blinds are now ${smallBlind} ${bigBlind}`;
  if (ante) text += ` with an ante of ${ante}`;
  return text + '.';
}

/**
 * Announce a new blind level (sound + voice)
 */
export function announceLevelChange(level, options = {}) {
  const { sound = true, voice = true } = options;
  if (sound) playSound('wooga', 600);
  if (voice) speakAnnouncement(buildAnnouncement(level));
}

/**
 * Early warning before level ends (1 minute by default)
 */
export function playEarlyWarning(options = {}) {
  const { sound = true, voice = true, secondsRemaining = 60 } = options;
  if (sound) playSound('beep', 200);
  if (voice) {
    const min = Math.floor(secondsRemaining / 60);
    const text = min === 1 ? 'One minute remaining.' : `${secondsRemaining} seconds remaining.`;
    speakAnnouncement(text);
  }
}

/**
 * Call-the-clock countdown - 30 second forced action
 */
let callClockInterval = null;

export function startCallClock(durationSec = 30, onTick, onDone) {
  stopCallClock();
  let remaining = durationSec;
  speakAnnouncement(`Call the clock. ${durationSec} seconds.`);
  if (onTick) onTick(remaining);

  callClockInterval = setInterval(() => {
    remaining--;
    if (remaining <= 10 && remaining > 0) {
      playSound('beep', 150);
    }
    if (onTick) onTick(remaining);
    if (remaining <= 0) {
      stopCallClock();
      playSound('wooga', 600);
      speakAnnouncement('Time. Your hand is dead.');
      if (onDone) onDone();
    }
  }, 1000);
}

export function stopCallClock() {
  if (callClockInterval) {
    clearInterval(callClockInterval);
    callClockInterval = null;
  }
}

/**
 * Quick utility announcements
 */
export const announce = {
  lastHand: () => speakAnnouncement('Last hand of the level.'),
  breakTime: () => speakAnnouncement('Break time. Take five.'),
  resumePlay: () => speakAnnouncement('Resume play. Cards in the air.'),
  paused: () => speakAnnouncement('Timer paused.'),
  resumed: () => speakAnnouncement('Timer resumed.'),
  payouts: (place, amount) => speakAnnouncement(`${place} place pays ${amount}.`),
  playerEliminated: (name, remaining) => speakAnnouncement(`${name} is eliminated. ${remaining} players remaining.`),
  custom: (text) => speakAnnouncement(text),
};

/**
 * Multi-warning system - fires at 5min, 1min, last hand
 */
export function checkMultiWarning(secondsRemaining, settings = {}) {
  const fired = settings._fired || {};

  // 5 minute warning
  if (secondsRemaining <= 300 && secondsRemaining > 290 && !fired.fiveMin) {
    fired.fiveMin = true;
    if (settings.fiveMinEnabled !== false) {
      playSound('beep', 200);
      speakAnnouncement('Five minutes remaining in this level.');
    }
  }
  // 1 minute warning
  if (secondsRemaining <= 60 && secondsRemaining > 50 && !fired.oneMin) {
    fired.oneMin = true;
    if (settings.oneMinEnabled !== false) {
      playSound('beep', 200);
      speakAnnouncement('One minute remaining. Last hand of the level.');
    }
  }
  // 30 second warning
  if (secondsRemaining <= 30 && secondsRemaining > 25 && !fired.thirtySec) {
    fired.thirtySec = true;
    if (settings.thirtySecEnabled) {
      playSound('beep', 150);
    }
  }

  // Reset on new level (when over 5 min again)
  if (secondsRemaining > 320) {
    fired.fiveMin = false;
    fired.oneMin = false;
    fired.thirtySec = false;
  }

  return fired;
}

/**
 * Voice settings storage (localStorage)
 */
const VOICE_SETTINGS_KEY = 'apk_voice_settings';

export function getVoiceSettings() {
  try {
    const stored = localStorage.getItem(VOICE_SETTINGS_KEY);
    return stored ? JSON.parse(stored) : {
      enabled: true,
      soundEnabled: true,
      voiceRate: 1,
      voicePitch: 1,
      voiceVolume: 1,
      fiveMinEnabled: true,
      oneMinEnabled: true,
      thirtySecEnabled: false,
    };
  } catch {
    return { enabled: true, soundEnabled: true };
  }
}

export function saveVoiceSettings(settings) {
  try {
    localStorage.setItem(VOICE_SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
}
